import { z } from "zod";

import {
  amount,
  date,
  reference,
  text,
  time,
} from "@frontend/lib/form/validators";
import { msg } from "@lingui/core/macro";

const count = z.number().int().nonnegative().nullable();

// A duration as the legacy form writes it, 7h30 or 0h00.
const DURATION = /^\d{1,3}h[0-5]\d$/;

const duration = z
  .string()
  .regex(DURATION, { error: () => text(msg`Enter a duration as 7h30`) })
  .nullable();

export const clientContactSchema = z.object({
  id: z.number().int().nullable().default(null),
  contactId: reference.default(null),
  typeId: reference.default(null),
});

export const accountantSchema = z.object({
  id: z.number().int().nullable().default(null),
  employeeId: reference.default(null),
  typeId: reference.default(null),
});

export const plannedExpenseSchema = z.object({
  id: z.number().int().nullable().default(null),
  typeId: reference.default(null),
  quantity: count.default(null),
  unitPrice: amount.default(null),
  recurrenceId: reference.default(null),
});

export const commercialConditionSchema = z.object({
  paymentTypeId: reference.default(null),
  interventionPaymentPeriodId: reference.default(null),
  paymentEmail: z.string().nullable().default(null),
  paymentEmailCopyTo: z.string().nullable().default(null),
});

export const subscriptionSchema = z.object({
  initialAffiliateId: reference.default(null),
  billingProjectId: reference.default(null),
});

export const hourRangeSchema = z.object({
  id: z.number().int().nullable().default(null),
  begin: time.default(null),
  end: time.default(null),
});

export const dailyScheduleSchema = z.object({
  arrival: time.default(null),
  departure: time.default(null),
  hours: z.array(hourRangeSchema).default([]),
});

export const interventionSchema = z.object({
  taskTypeId: reference.default(null),
  subTaskTypeId: reference.default(null),
  dateBegin: date.default(null),
  dateEnd: date.default(null),
  interventionDays: count.default(null),
  refundLunch: z.boolean().default(false),
  refundRide: z.boolean().default(false),
  rideDuration: count.default(null),
  units: z.number().nonnegative().nullable().default(null),
  unitTypeId: reference.default(null),
  voucherNumber: z.string().nullable().default(null),
  recurrence: z.string().nullable().default(null),
  exploitationComments: z.string().nullable().default(null),
  estimatedHours: duration.default(null),
  estimatedDays: count.default(null),
  estimatedWorkers: count.default(null),
  estimatedValuedHours: duration.default(null),
  startingSiteId: reference.default(null),
  subscriptionId: reference.default(null),
  plannedExpenses: z.array(plannedExpenseSchema).default([]),
  dailySchedule: dailyScheduleSchema,
});

export const projectSchema = z
  .object({
    type: z.enum(["subscription", "intervention"]),
    lockVersion: z.string().nullable().default(null),
    name: z.string().nullable().default(null),
    statusId: reference.default(null),
    serviceGroupId: reference.default(null),
    clientId: reference.default(null),
    locationId: reference.default(null),
    deliveryLocationId: reference.default(null),
    billingAddressId: reference.default(null),
    billingContactId: reference.default(null),
    billingContactInBill: z.boolean().default(false),
    billingAttorneyId: reference.default(null),
    billingClientReference: z.string().nullable().default(null),
    billingClientReferenceInBill: z.boolean().default(false),
    billingWording: z.string().nullable().default(null),
    billingComments: z.string().nullable().default(null),
    comments: z.string().nullable().default(null),
    quotationNumber: z.string().nullable().default(null),
    billingTypeJustification: z.string().nullable().default(null),
    clientContacts: z.array(clientContactSchema).default([]),
    accountants: z.array(accountantSchema).default([]),
    commercialCondition: commercialConditionSchema,
    subscription: subscriptionSchema,
    intervention: interventionSchema,
  })
  .superRefine((values, ctx) => {
    const required = (path: string[]): void => {
      ctx.addIssue({
        code: "custom",
        message: text(msg`Choose a value`),
        path,
      });
    };

    if ("subscription" === values.type && null === values.statusId) {
      required(["statusId"]);
    }

    if (null === values.clientId) {
      required(["clientId"]);
    }

    if (null === values.locationId) {
      required(["locationId"]);
    }

    if ("intervention" !== values.type) {
      return;
    }

    const { dateBegin, dateEnd, startingSiteId } = values.intervention;

    if (null === startingSiteId) {
      required(["intervention", "startingSiteId"]);
    }

    if (dateBegin && dateEnd && dateBegin > dateEnd) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`The end comes before the start`),
        path: ["intervention", "dateEnd"],
      });
    }
  });

export type ClientContactValues = z.infer<typeof clientContactSchema>;
export type AccountantValues = z.infer<typeof accountantSchema>;
export type PlannedExpenseValues = z.infer<typeof plannedExpenseSchema>;

export type HourRangeValues = z.infer<typeof hourRangeSchema>;

export type DailyScheduleValues = z.infer<typeof dailyScheduleSchema>;
export type CommercialConditionValues = z.infer<
  typeof commercialConditionSchema
>;
export type SubscriptionValues = z.infer<typeof subscriptionSchema>;
export type InterventionValues = z.infer<typeof interventionSchema>;
export type ProjectValues = z.infer<typeof projectSchema>;
